import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import {
  ContentSegmentTranslation,
  ContentSegmentTransforms,
  ContentSegmentTranslatable,
} from '@go-shared/types/translation';
import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeContentSegmentTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandContentSegment, BrandSegment } from '@go-shared/types/brand';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';

interface ContentSegmentTranslationDataBundle {
  segments: BrandContentSegment[];
  segmentTranslations: ContentSegmentTranslation[];
  segmentsMap: { [key: string]: BrandContentSegment };
  segmentTranslationsMap: { [key: string]: ContentSegmentTranslation };
}

interface ContentSegmentTranslationForm {
  dirty: boolean;
  transforms: ContentSegmentTransforms;
}

interface ChangeContentSegmentFormAction {
  active?: boolean;
  valueChange?: { key: keyof ContentSegmentTranslatable; value: string };
}

type ContentSegmentTranslationsPageProps = RouteComponentProps;

export default function SegmentTranslations(props: ContentSegmentTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<ContentSegmentTranslationDataBundle | undefined>(undefined);
  const [activeSegment, setActiveSegment] = useState<BrandSegment | undefined>(undefined);
  const [segmentTranslationForm, setSegmentTranslationForm] = useState<
    ContentSegmentTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();
  const serverInfo = useServerInfo();
  const { languages, brand } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active segment if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveSegment(state => {
      const brandSegment = dataBundle.segments[0];
      if (!state) return brandSegment;
      const refreshedSegment = dataBundle.segmentsMap[state._id];
      return refreshedSegment || brandSegment;
    });
  }, [dataBundle]);

  // this will reset the form each time a new segment is clicked
  useEffect(() => {
    resetSegmentsForm();
  }, [activeSegment]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.segments, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredSegments = !dataBundle ? [] : fuseSearchResults || dataBundle.segments;

  async function fetchDataBundle() {
    try {
      const segments = brand.contentSegments;
      const segmentTranslations = await getTranslations(activeProperty._id, 'contentSegment');

      const segmentsMap = mapBy(segments, '_id');
      const segmentTranslationsMap = mapBy(segmentTranslations, 'refers');

      setDataBundle({
        segments,
        segmentTranslations,
        segmentsMap,
        segmentTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetSegmentsForm() {
    const transforms = activeSegment
      ? makeContentSegmentTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.segmentTranslationsMap[activeSegment._id]
        )
      : undefined;

    if (!transforms) {
      setSegmentTranslationForm(undefined);
      return;
    }

    const formData: ContentSegmentTranslationForm = {
      dirty: false,
      transforms,
    };

    setSegmentTranslationForm(formData);
  }

  function changeSegmentForm(lang: string, change: ChangeContentSegmentFormAction) {
    if (!segmentTranslationForm) return;

    const updatedForm = { ...segmentTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setSegmentTranslationForm(updatedForm);
  }

  async function handleSubmitSegmentForm() {
    if (
      !segmentTranslationForm ||
      !activeSegment ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    ) {
      return;
    }

    const translation = generateDefaultNewTranslation(activeSegment._id, 'contentSegment');

    translation.transforms = segmentTranslationForm.transforms;

    await replaceTranslationByRefer(activeSegment._id, translation);
    await fetchDataBundle();
    setSegmentTranslationForm({ ...segmentTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="contentSegments" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredSegments.map(segment => (
            <div
              key={segment._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeSegment && safeIdCompare(activeSegment._id, segment._id) ? 'active' : ''
              }`}
              onClick={() => setActiveSegment(segment)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{segment.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredSegments.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No segments found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeSegment && segmentTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This segment cannot be modified unless you're in a brand instance and a brand
                  admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeSegment.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({segmentTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !segmentTranslationForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeSegmentForm(language.key, {
                          active: !segmentTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        segmentTranslationForm.transforms[language.key].active ? 'eye' : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !segmentTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={segmentTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeSegmentForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitSegmentForm()}
                variant="navy"
                disabled={
                  !segmentTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!segmentTranslationForm.dirty}
                inverse
                onClick={() => resetSegmentsForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (segmentTranslationForm && segmentTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={segmentTranslationForm && segmentTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="segments"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
